import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleColorModel } from "../../models/chart-style/chart-style-color.model";
import { TradeOrderDataModel } from "../../models/draw-trade-order/trade-order-data.model";
import { TradeOrderPriceHelper } from "../../helpers/trade-order-price-helper";

import { IComponent } from "../component.interface";
import { TradePlotLineComponent } from "../trade-plot-line.component";
import { BracketOrderProfitTargetPlotLine } from "./bracket-order-profit-target-plot-line.component";
import { BracketOrderStopLossPlotLine } from "./bracket-order-stop-loss-plot-line.component";
import { OrderBracketManagerComponent } from "../order-bracket-manager.component";
import { DrawTradeOrdersComponent } from "../draw-trade-orders.component";
import { QuoteType, TradeModel, TradeOrderType } from "../../models/trade/trade.model";

export class BracketTradePlotLineComponent implements IComponent {
    public id: string;
    public lastPrice: number;
    public isActive = false;
    public profitTargetPlotLine: BracketOrderProfitTargetPlotLine | null = null;
    public stopLossPlotLines: Array<BracketOrderStopLossPlotLine> = [];
    public requestingProfitTarget = false;
    public requestingStopLoss = false;

    private base: IChartContainer;
    private tradePlotLine: TradePlotLineComponent;
    private tradeOrders: Array<TradeOrderDataModel>;
    private removed = false;
    private bracketIcon: any = null;
    private orderBracketManager: OrderBracketManagerComponent | null = null;

    constructor(base: IChartContainer, tradePlotLine: TradePlotLineComponent) {
        this.base = base;
        this.tradePlotLine = tradePlotLine;
        this.orderBracketManager = this.base.getComponentsByType(OrderBracketManagerComponent)?.[0] as OrderBracketManagerComponent;
        this.id = 'limit-pt-sl-' + tradePlotLine.model.id;
        this.lastPrice = base.tiChartSeries.lastPrice;

        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.lastPrice = lastPrice;
        });
    }

    public get isLong() {
        return this.tradePlotLine.model.isLong;
    }

    public get isPending(): boolean {
        return this.tradePlotLine.isPending;
    }

    public get elementWidth() {
        return this.tradePlotLine.elementWidth;
    }

    public get left() {
        return this.tradePlotLine.left;
    }

    public get model(): TradeOrderDataModel {
        return this.tradePlotLine.model;
    }

    public get hide() {
        return this.tradePlotLine.hide;
    }

    public get entryOrderType(): 'limit' | 'stop' | 'stoplimit' {
        return this.model.tradeType?.toLowerCase() as 'limit' | 'stop' | 'stoplimit';
    }

    get hasProfitTargetOrder(): boolean {
        return this.profitTargetPlotLine !== null;
    }

    get hasStopLossOrder(): boolean {
        return this.stopLossPlotLines.length > 0;
    }

    get hasBracketOrder(): boolean {
        return this.hasStopLossOrder || this.hasProfitTargetOrder;
    }

    public get position() {
        return {
            averageCost: this.tradePlotLine.model.price,
            shares: this.tradePlotLine.model.shares,
            accountName: this.tradePlotLine.model.accountName,
            symbol: this.base.tiChartSeries.symbol,
        };
    }

    public onExtreamChange() {
        if (this.removed) return;
        this.profitTargetPlotLine?.onExtreamChange();
        this.stopLossPlotLines.forEach(s => s.onExtreamChange());
    }

    public onRedraw() {
        if (this.removed) return;
        this.profitTargetPlotLine?.onRedraw();
        this.stopLossPlotLines.forEach(s => s.onRedraw());
    }

    public getSLOrPTTradeOrders(): Array<TradeOrderDataModel> {
        const order = this.model;
        return this.tradeOrders.filter(x =>
            x.accountName == order.accountName &&
            x.ocaGroup !== '' && x.ocaGroup != null &&
                (x.ocaGroup == order.id || x.entryTime?.getTime() === order.entryTime?.getTime())
        );
    }

    public updateAllTradeOrder(tradeOrders: Array<TradeOrderDataModel>) {
        if (this.removed) return;
        this.tradeOrders = tradeOrders;

        if (this.anyPlotLinesMoving()) {
            return;
        }

        this.drawProfitTargetAndStopLoss();
    }

    public remove() {
        this.removed = true;
        this.removeProfitTarget();
        this.removeStopLosses();

        try {
            this.bracketIcon?.destroy();
            this.bracketIcon = null;
        } catch {

        }

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
    }

    public activate(allTradeOrders: Array<TradeOrderDataModel>) {
        this.isActive = true;
        this.updateAllTradeOrder(allTradeOrders);
        if (!this.tradePlotLine.isPending) {
            this.tradePlotLine.enable();
        }
        this.profitTargetPlotLine?.enable();
        this.stopLossPlotLines.forEach(s => s.enable());
    }

    public setBracketEnabled(enabled: boolean) {
        const opacity = enabled ? 1 : 0.3;
        this.bracketIcon?.attr({ 'opacity': opacity });
    }

    public deactivate() {
        this.isActive = false;
        if (!this.tradePlotLine.isPending) {
            this.tradePlotLine.disable();
        }
        this.profitTargetPlotLine?.disable();
        this.stopLossPlotLines.forEach(s => s.disable());
    }

    private drawProfitTargetAndStopLoss() {
        const orders = this.getSLOrPTTradeOrders();

        const profitTarget = this.isLong
            ? orders.find(x => x.tradeType?.toLowerCase() === 'limit' && !x.isLong)
            : orders.find(x => x.tradeType?.toLowerCase() === 'limit' && x.isLong);

        const stopLoss = this.isLong
            ? orders.find(x => x.tradeType?.toLowerCase() === 'stop' && !x.isLong)
            : orders.find(x => x.tradeType?.toLowerCase() === 'stop' && x.isLong);

        if (profitTarget) {
            this.createProfitTarget(profitTarget);
        } else {
            this.removeProfitTarget();
        }

        if (stopLoss) {
            this.createStopLoss(stopLoss);
        } else {
            this.removeStopLosses();
        }
    }

    public createProfitTarget(model: TradeOrderDataModel) {
        this.removeProfitTarget();
        if (this.removed) return;
        this.requestingProfitTarget = false;
        this.profitTargetPlotLine = new BracketOrderProfitTargetPlotLine(this.base, this, this.left, model);
        // this.tradePlotLine.divElement?.querySelector('.profit-target-button')?.classList.add('hidden');
    }

    public createStopLoss(model: TradeOrderDataModel) {
        this.removeStopLosses();
        if (this.removed) return;
        this.requestingStopLoss = false;
        const sl = new BracketOrderStopLossPlotLine(this.base, this, this.left, model);
        this.stopLossPlotLines.push(sl);
        // this.tradePlotLine.divElement?.querySelector('.stop-loss-button')?.classList.add('hidden');
    }

    public removeProfitTarget() {
        this.profitTargetPlotLine?.remove();
        this.profitTargetPlotLine = null;
        // this.tradePlotLine.divElement?.querySelector('.profit-target-button')?.classList.remove('hidden');
    }

    public removeStopLosses() {
        for (let s of this.stopLossPlotLines) {
            s.remove();
            s = null;
        }
        this.stopLossPlotLines = [];
        // this.tradePlotLine.divElement?.querySelector('.stop-loss-button')?.classList.remove('hidden');
    }

    public anyPlotLinesMoving(): boolean {
        const ptMoving = this.profitTargetPlotLine?.moveMode ?? false;
        const slMoving = this.stopLossPlotLines.some(x => x?.moveMode ?? false);
        return ptMoving || slMoving;
    }

    public getColor(positive: boolean): ChartStyleColorModel {
        if (positive) {
            return new ChartStyleColorModel({ r: 38, g: 142, b: 55 });
        } else {
            return new ChartStyleColorModel({ r: 240, g: 54, b: 68 });
        }
    }

    public getOrderLineWidth(entryTime: Date | null): number {
        let xValue: number | null = null;
        let width = this.base.tiChartSeries.element.clientWidth;
        if (entryTime) {
            xValue = this.base.tiChartSeries.getXValueByDateTime(entryTime);
        }

        const mainChartSerie: any = this.base.tiChartSeries.highStockChart?.series?.[0];
        const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0];

        if (mainChartSerie?.processedXData?.length && xValue) {
            if (mainChartSerie && xAxis && mainChartSerie.processedXData.some((x: number) => x == xValue)) {
                const currentXValueWidth = xAxis.toPixels(xValue) ?? 0;
                let widthToReduce = 35;
                const chartExtraSmall = this.base.isExtraSmall();
                if (this.base.tiChartSeries?.chartStyleSettings.showSmartRiskLevel && !chartExtraSmall) {
                    widthToReduce += 100;
                } else {
                    widthToReduce += 70;
                }
                width = Math.abs(this.base.tiChartSeries.element.clientWidth - currentXValueWidth - widthToReduce);
            } else if (mainChartSerie.processedXData[mainChartSerie.processedXData.length - 1] < xValue) {
                width = 0;
            }
        }
        return width;
    }

    public setLineRight(cssSelector: string, toTheRight: boolean, entryTime?: Date) {
        const rightValue = 6;
        const linewidth = this.getOrderLineWidth(entryTime);
        this.base.chartWrapper?.querySelectorAll<HTMLElement>(cssSelector).forEach((el: HTMLElement) => {
            const line = el.findFirst('.line');
            if (line) {
                if (toTheRight) {
                    line.style.width = (linewidth + rightValue) + 'px';
                    line.style.right = `-${rightValue}px`;
                } else {
                    line.style.width = linewidth + 'px';
                    line.style.right = '';
                }
            }
        });
    }

    public addBracketIcon() {
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        try {
            this.bracketIcon?.destroy();
            this.bracketIcon = null;
        } catch { }

        if (this.hide) {
            this.setBracketEnabled(!this.tradePlotLine.disabled);
            return;
        }

        const color = new ChartStyleColorModel({ r: 0, g: 79, b: 208 });

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        const width = this.elementWidth;

        const EXTRA_TOP = this.base.element.containClass('one')
            ? (this.base.marketingWebChartMode ? 38 : 26)
            : (this.base.marketingWebChartMode ? 58 : 45);
        const currentY = yAxis.toPixels(this.position.averageCost, true) + EXTRA_TOP;
        const left = this.left + width + 9;

        this.bracketIcon = renderer.circle(left, Math.floor(currentY) + 1, 3)
            .attr({
                'fill': 'white',
                'stroke': color.toRGB(),
                'stroke-width': 1.5,
                'zIndex': 12
            })
            .add();

        this.setBracketEnabled(!this.tradePlotLine.disabled);
    }

    private requestRemove(tradeId: string) {
        const drawTradeOrdersComp = this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent;
        if (drawTradeOrdersComp?.removeTradeIds.includes(tradeId)) return;
        drawTradeOrdersComp?.removeTradeIds.push(tradeId);
        this.base.chartService.requestRemoveChartTrade(tradeId);
        this.base.tiChartSeries.preventDragging = false;
    }

    public submitProfitTarget(): void {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) return;
        const tradeModel = this.isLong ? this.generateOrder_LongProfitTarget(yAxis) : this.generateOrder_ShortProfitTarget(yAxis);
        this.base.chartService.requestManualTrade(tradeModel);
        this.requestingProfitTarget = true;
    }

    public submitStopLoss(): void {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) return;
        const tradeModel = this.isLong ? this.generateOrder_LongStopLoss(yAxis) : this.generateOrder_ShortStopLoss(yAxis);
        this.base.chartService.requestManualTrade(tradeModel);
        this.requestingStopLoss = true;
    }

    private getTopPixels(): number {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return 0;
        let top = yAxis.toPixels(this.position.averageCost, true);
        if (this.base.element.containClass('one')) {
            top += this.base.marketingWebChartMode ? 26 : 14;
        } else {
            top += this.base.marketingWebChartMode ? 45 : 33;
        }
        return top;
    }

    private getProfitTargetPrice(yAxis: any, isLong: boolean): number {
        return TradeOrderPriceHelper.calcProfitTargetPrice(
            this.position.averageCost,
            this.getTopPixels(),
            isLong,
            yAxis,
            this.base.tiChartSeries.currentTimeframe,
            this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel,
            this.base.tiChartSeries.chartStyleSettings?.orderProfitTargetPercentDefault
        );
    }

    private getStopLossPrice(yAxis: any, isLong: boolean): number {
        return TradeOrderPriceHelper.calcStopLossPrice(
            this.position.averageCost,
            this.getTopPixels(),
            isLong,
            yAxis,
            this.base.tiChartSeries.currentTimeframe,
            this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel,
            this.base.tiChartSeries.chartStyleSettings?.orderStopLossPercentDefault
        );
    }

    private generateOrder_LongProfitTarget(yAxis: any): TradeModel {
        const price = this.getProfitTargetPrice(yAxis, false);
        return new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.model.id,
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 13
        });
    }

    private generateOrder_ShortProfitTarget(yAxis: any): TradeModel {
        const price = this.getProfitTargetPrice(yAxis, true);
        return new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.model.id,
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 13
        });
    }

    private generateOrder_LongStopLoss(yAxis: any): TradeModel {
        const price = this.getStopLossPrice(yAxis, false);
        return new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.model.id,
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 12
        });
    }

    private generateOrder_ShortStopLoss(yAxis: any): TradeModel {
        const price = this.getStopLossPrice(yAxis, true);
        return new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.model.id,
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 12
        });
    }

    public closeProfitTarget() {
        if (this.profitTargetPlotLine?.tradeId) {
            this.requestRemove(this.profitTargetPlotLine.tradeId);
        }
        this.profitTargetPlotLine?.disable();
    }

    public closeStopLoss() {
        for (const s of this.stopLossPlotLines) {
            if (s.disabled || s.removed) continue;

            if (s.tradeId) {
                this.requestRemove(s.tradeId);
            }

            s.removed = true;
            s.disable();
        }
    }
}
